import { useMutation } from '@tanstack/react-query';
import { useCallback, useRef } from 'react';

import { UploadDocumentV2Request, UploadStreamResponse } from '@/types/document';

import { useAuth } from '@/context/AuthProvider';
import { documentService, UploadDocumentV2FileRequest } from '@/services/documentService';

interface UseDocumentUploadV2Params {
  onProgress?: (response: UploadStreamResponse) => void;
  onComplete?: (response: UploadStreamResponse) => void;
  onError?: (error: string) => void;
}

interface UseDocumentUploadV2Return {
  uploadDocument: (request: UploadDocumentV2Request) => void;
  uploadFile: (request: UploadDocumentV2FileRequest) => void;
  isUploading: boolean;
  cancelUpload: () => void;
}

export const useDocumentUploadV2 = ({
  onProgress,
  onComplete,
  onError,
}: UseDocumentUploadV2Params): UseDocumentUploadV2Return => {
  const { session } = useAuth();
  const abortControllerRef = useRef<AbortController | null>(null);
  const retryCountRef = useRef<number>(0);
  const maxRetries = 3;

  const uploadMutation = useMutation({
    mutationFn: async (request: UploadDocumentV2Request | UploadDocumentV2FileRequest) => {
      const performUpload = async (retryCount: number): Promise<void> => {
        // Create abort controller for cancellation
        const abortController = new AbortController();
        abortControllerRef.current = abortController;
        try {
          let fetchResponse: Response;

          if ('file' in request) {
            // File upload request
            const formData = new FormData();
            formData.append('file', request.file);

            if (request.data_type) {
              formData.append('data_type', request.data_type);
            }

            if (request.location_ids) {
              formData.append('location_ids', JSON.stringify(request.location_ids));
            }

            if (request.tags) {
              formData.append('tags', JSON.stringify(request.tags));
            }

            fetchResponse = await documentService.uploadDocumentV2Stream(
              request,
              session?.access_token || '',
              abortController.signal
            );
          } else {
            fetchResponse = await documentService.uploadDocumentV2Stream(
              request,
              session?.access_token || '',
              abortController.signal
            );
          }

          if (!fetchResponse.ok) {
            // Handle 502/503/504 errors with retry
            if ([502, 503, 504].includes(fetchResponse.status) && retryCount < maxRetries) {
              const delay = Math.min(1000 * 2 ** retryCount, 10000); // Exponential backoff, max 10s

              onProgress?.({
                status: 'processing',
                message: `Connection issue, retrying in ${delay / 1000}s... (attempt ${
                  retryCount + 1
                }/${maxRetries + 1})`,
              });

              await new Promise<void>(resolve => {
                setTimeout(() => resolve(), delay);
              });
              return performUpload(retryCount + 1);
            }

            const errorText = await fetchResponse.text();
            throw new Error(`HTTP error! status: ${fetchResponse.status}, message: ${errorText}`);
          }

          if (!fetchResponse.body) {
            throw new Error('No response body');
          }

          const reader = fetchResponse.body.getReader();
          const decoder = new TextDecoder();

          let buffer = '';
          let lastActivityTime = Date.now();
          const activityTimeoutMs = 120000; // 2 minutes of inactivity
          let lastStatus = '';

          const processStream = async (): Promise<void> => {
            const { done, value } = await reader.read();

            if (done) {
              return;
            }

            lastActivityTime = Date.now(); // Reset activity timer
            const chunk = decoder.decode(value, { stream: true });
            buffer += chunk;

            // Process complete lines
            const lines = buffer.split('\n');
            buffer = lines.pop() || ''; // Keep incomplete line in buffer

            for (const line of lines) {
              const trimmedLine = line.trim();

              if (trimmedLine.startsWith('data: ')) {
                const data = trimmedLine.slice(6); // Remove 'data: ' prefix

                if (data === '[DONE]') {
                  return;
                }

                try {
                  const streamResponse: UploadStreamResponse = JSON.parse(data);

                  // Filter out padding messages (backend sends these to force stream flushing)
                  if (streamResponse.type === 'padding') {
                    // Skip padding messages without using continue
                  } else {
                    if (streamResponse.status === 'error') {
                      onError?.(streamResponse.message);
                      throw new Error(streamResponse.message);
                    }

                    if (streamResponse.status === 'completed') {
                      onComplete?.(streamResponse);
                      return;
                    }

                    // Track status changes to detect if we're stuck (ignore heartbeat messages)
                    if (
                      streamResponse.status !== 'heartbeat' &&
                      lastStatus !== streamResponse.status
                    ) {
                      lastStatus = streamResponse.status;
                    }

                    // Send progress updates for all non-padding messages
                    onProgress?.(streamResponse);
                  }
                } catch (parseError) {
                  // Skip invalid JSON lines
                }
              }
            }

            // Check for inactivity timeout
            if (Date.now() - lastActivityTime > activityTimeoutMs) {
              const timeoutMessage = `Stream timeout: No activity for 2 minutes. Last status: ${lastStatus}`;
              throw new Error(timeoutMessage);
            }

            // Continue processing stream recursively
            return processStream();
          };

          await processStream();

          // If we reach here without any progress callbacks, it means the stream completed
          // without sending any data. This might indicate the backend processed immediately.
          onComplete?.({
            status: 'completed',
            message: 'Processing completed successfully',
            progress: 1.0,
          });
        } catch (error) {
          if (error.name === 'AbortError') {
            throw new Error('Upload cancelled');
          }

          // Handle network timeouts and connection errors with retry
          if (
            (error instanceof TypeError && error.message.includes('fetch')) ||
            error.message.includes('Stream timeout') ||
            error.message.includes('502')
          ) {
            if (retryCount < maxRetries) {
              const delay = Math.min(1000 * 2 ** retryCount, 10000);

              onProgress?.({
                status: 'processing',
                message: `Connection lost, retrying in ${delay / 1000}s... (attempt ${
                  retryCount + 1
                }/${maxRetries + 1})`,
              });

              await new Promise<void>(resolve => {
                setTimeout(() => resolve(), delay);
              });
              return performUpload(retryCount + 1);
            }
            throw new Error(
              'Upload timed out after multiple retries. The document is being processed and will appear when complete. Please refresh the page in a few minutes.'
            );
          }

          // Handle timeout errors specifically
          if (error.message.includes('timeout') || error.message.includes('502')) {
            throw new Error(
              'Upload timed out. The document is being processed and will appear when complete. Please refresh the page in a few minutes.'
            );
          }

          onError?.(error.message || 'Unknown error occurred');
          throw error;
        } finally {
          abortControllerRef.current = null;
        }
      };

      // Start the upload with retry logic
      retryCountRef.current = 0;
      return performUpload(0);
    },
    onError: error => {
      onError?.(error.message || 'Upload failed');
    },
  });

  const uploadDocument = useCallback(
    (request: UploadDocumentV2Request) => {
      uploadMutation.mutate(request);
    },
    [uploadMutation]
  );

  const uploadFile = useCallback(
    (request: UploadDocumentV2FileRequest) => {
      uploadMutation.mutate(request);
    },
    [uploadMutation]
  );

  const cancelUpload = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    uploadMutation.reset();
  }, [uploadMutation]);

  return {
    uploadDocument,
    uploadFile,
    isUploading: uploadMutation.isPending,
    cancelUpload,
  };
};
